import { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// Public customer signup form - no auth required
// Accessed via /signup/:token
export default function CustomerSignupForm({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signupData, setSignupData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    sms_consent: true
  });

  // Load signup data on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid signup link');
      setLoading(false);
      return;
    }

    fetchSignupData();
  }, [token]);

  const fetchSignupData = async () => {
    try {
      const response = await fetch(`/api/customer-signup?token=${token}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load signup form');
      }

      setSignupData(data.signup);
      // Pre-fill phone from the signup record
      setFormData(prev => ({
        ...prev,
        phone: data.signup.phone || ''
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name.trim()) {
      setError('First name is required');
      return;
    }
    // Build full name from first/last
    formData.name = `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim();

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/customer-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'complete',
          token,
          ...formData
        })
      });

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        throw new Error('Server returned an invalid response. Please try again.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Common styles
  const colors = {
    bg: '#0a0a0a',
    bgCard: '#141414',
    text: '#ffffff',
    textMuted: '#888888',
    accent: '#f97316',
    border: '#333333',
    bgInput: '#1a1a1a',
    success: '#10b981',
    error: '#ef4444'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.text,
    fontSize: '1rem',
    outline: 'none'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: colors.textMuted,
    fontSize: '0.9rem'
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{ textAlign: 'center', color: colors.text }}>
          <Loader size={48} style={{ animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          <div style={{ marginTop: '1rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired link)
  if (error && !signupData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          border: `1px solid ${colors.border}`
        }}>
          <AlertCircle size={48} color={colors.error} />
          <h2 style={{ color: colors.text, margin: '1rem 0 0.5rem' }}>Link Invalid</h2>
          <p style={{ color: colors.textMuted }}>{error}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          border: `1px solid ${colors.border}`
        }}>
          <CheckCircle size={64} color={colors.success} />
          <h2 style={{ color: colors.text, margin: '1rem 0 0.5rem' }}>Thank You!</h2>
          <p style={{ color: colors.textMuted }}>
            Your information has been submitted to {signupData?.company?.name || 'the company'}.
            They will be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  // Form view
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '2rem',
        paddingTop: '2rem'
      }}>
        {signupData?.company?.logo_url && (
          <img
            src={signupData.company.logo_url}
            alt="Logo"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              objectFit: 'contain',
              marginBottom: '1rem'
            }}
          />
        )}
        <h1 style={{ color: colors.text, margin: 0, fontSize: '1.5rem' }}>
          {signupData?.company?.name || 'Customer Signup'}
        </h1>
        <p style={{ color: colors.textMuted, marginTop: '0.5rem' }}>
          Please fill out your information below
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '100%',
          border: `1px solid ${colors.border}`
        }}
      >
        {error && (
          <div style={{
            padding: '0.75rem 1rem',
            background: '#7f1d1d40',
            border: `1px solid ${colors.error}`,
            borderRadius: '8px',
            color: colors.error,
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {/* Name */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <label style={labelStyle}>
              First Name <span style={{ color: colors.error }}>*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="John"
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Last Name</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Smith"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Phone */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            style={inputStyle}
          />
        </div>

        {/* Email */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            style={inputStyle}
          />
        </div>

        {/* Address */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Street Address</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="123 Main St"
            style={inputStyle}
          />
        </div>

        {/* City, State, Zip - Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <div>
            <label style={labelStyle}>City</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Austin"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>State</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="TX"
              maxLength={2}
              style={{ ...inputStyle, textTransform: 'uppercase' }}
            />
          </div>
          <div>
            <label style={labelStyle}>ZIP</label>
            <input
              type="text"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              placeholder="78701"
              maxLength={10}
              style={inputStyle}
            />
          </div>
        </div>

        {/* SMS Consent */}
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          cursor: 'pointer',
          fontSize: '0.85rem',
          color: '#b0b0b0',
          padding: '0.75rem',
          background: colors.bgInput,
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          marginBottom: '1.5rem'
        }}>
          <input
            type="checkbox"
            checked={formData.sms_consent}
            onChange={(e) => setFormData(prev => ({ ...prev, sms_consent: e.target.checked }))}
            style={{
              width: '20px',
              height: '20px',
              marginTop: '2px',
              accentColor: '#F05A28',
              cursor: 'pointer',
              flexShrink: 0
            }}
          />
          <span>
            I agree to receive text messages from <strong style={{ color: '#e0e0e0' }}>{signupData?.company?.name || 'this business'}</strong> about
            my appointments, quotes, and invoices. Msg frequency varies. Reply STOP to opt out. Msg & data rates may apply.
            {' '}
            <a
              href="/sms-terms.html"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                color: '#F05A28',
                textDecoration: 'none'
              }}
            >
              SMS Terms
            </a>
          </span>
        </label>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '1rem',
            background: colors.accent,
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {submitting ? (
            <>
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Submitting...
            </>
          ) : (
            'Submit'
          )}
        </button>
      </form>

      {/* Footer */}
      <div style={{
        marginTop: '2rem',
        paddingBottom: '2rem',
        textAlign: 'center',
        color: colors.textMuted,
        fontSize: '0.8rem'
      }}>
        Powered by Work Van
      </div>
    </div>
  );
}
